import { z } from 'zod';

/**
 * ADR-1 — approval and sourcing are SEPARATE axes.
 *
 * `status` answers "does the screenplay require this?" — a judgement about the
 * script. `sourcing_status` answers "should we look for it now?" — a judgement
 * about the production. Collapsing them into one enum makes
 * approved-but-not-needed-yet, deferred, and paused-reruns unrepresentable, and
 * forces a producer to un-approve a correct interpretation just to stop matching.
 */
export const RequirementStatus = z.enum([
  'DRAFT',
  'PROPOSED',
  'APPROVED',
  'DISMISSED',
  'SUPERSEDED',
  'ARCHIVED',
]);
export type RequirementStatus = z.infer<typeof RequirementStatus>;

export const RequirementSourcingStatus = z.enum(['INACTIVE', 'ACTIVE', 'PAUSED']);
export type RequirementSourcingStatus = z.infer<typeof RequirementSourcingStatus>;

/**
 * Why a producer dismissed a proposed requirement (BI gap 25). `NOT_IN_SCRIPT` is
 * the model's false positive; `NOT_NEEDED` a production choice about a place that
 * IS in the script. Stored on the suppression tombstone; optional on the wire.
 */
export const RequirementDismissalReason = z.enum([
  'NOT_IN_SCRIPT',
  'DUPLICATE',
  'WRONG_TYPE',
  'NOT_NEEDED',
  'OTHER',
]);
export type RequirementDismissalReason = z.infer<typeof RequirementDismissalReason>;

/**
 * The ONE matching trigger. Every caller asks this rather than re-deriving the
 * condition, so the engine and the UI badge can never disagree about whether a
 * requirement is being sourced.
 */
export function isMatchable(
  status: RequirementStatus,
  sourcing: RequirementSourcingStatus,
): boolean {
  return status === 'APPROVED' && sourcing === 'ACTIVE';
}

/**
 * Whether a requirement carries ANYTHING a listing can be assessed against.
 *
 * Retrieval filters on the normalised type and the must-haves are what a
 * candidate is scored on. A requirement with neither — a manual one typed as
 * a title and nothing else — used to match the whole territory: every listing
 * became a candidate with nothing assessed, which read as 26 results and was
 * none. Such a requirement is not searchable yet, and the product says so
 * instead of searching. The creative axis joins this predicate the day the
 * embedding adapter lands; until then a description alone is no signal.
 */
export function hasMatchableSignal(input: {
  normalizedRequirementType: string | null;
  constraints: readonly { kind: string }[];
}): boolean {
  return (
    input.normalizedRequirementType !== null ||
    input.constraints.some((c) => c.kind === 'MUST_HAVE' || c.kind === 'EXCLUDE')
  );
}

/** Never a percentage in the UI — a band plus a closed reason. */
export const ConfidenceBand = z.enum(['HIGH', 'NEEDS_REVIEW', 'AMBIGUOUS']);
export type ConfidenceBand = z.infer<typeof ConfidenceBand>;

export const ConfidenceReason = z.enum([
  'MULTIPLE_SCRIPT_NAMES',
  'INT_EXT_UNCLEAR',
  'SAME_LOCATION_UNCLEAR',
  'CATEGORY_AMBIGUOUS',
  'INSUFFICIENT_DESCRIPTION',
  'CONFLICTING_SCENES',
  'MANUAL_REQUIREMENT',
  'OTHER',
]);
export type ConfidenceReason = z.infer<typeof ConfidenceReason>;

export const RequirementOrigin = z.enum([
  'BREAKDOWN',
  'MANUAL',
  'DISCOVERY',
  'MERGE',
  'SPLIT',
]);
export type RequirementOrigin = z.infer<typeof RequirementOrigin>;

/**
 * ADR-5 — how a freshly extracted requirement reconciles against one the
 * producer already ruled on, when a new screenplay version arrives. A UUID does
 * not carry semantic identity, so this classification is what preserves human
 * decisions across revisions.
 */
export const ReconciliationVerdict = z.enum([
  'EXACT',
  'LIKELY_SAME',
  'AMBIGUOUS',
  'NEW',
  'REMOVED',
]);
export type ReconciliationVerdict = z.infer<typeof ReconciliationVerdict>;

/**
 * A MANUAL requirement has no extraction lifecycle: a new screenplay version
 * failing to reproduce it says nothing, because extraction never produced it in
 * the first place. It can therefore never be classified REMOVED.
 */
export function canBeRemovedByReconciliation(origin: RequirementOrigin): boolean {
  return origin !== 'MANUAL';
}

export const RequirementEventAction = z.enum([
  'PROPOSED',
  'APPROVED',
  'EDITED',
  'DISMISSED',
  'RESTORED',
  'MERGED',
  'SPLIT',
  'SCENES_CHANGED',
  'CONSTRAINT_CHANGED',
  'SEARCH_CONTEXT_CHANGED',
  'SOURCING_ACTIVATED',
  'SOURCING_PAUSED',
  'SOURCING_DEACTIVATED',
  'SUPERSEDED',
]);
export type RequirementEventAction = z.infer<typeof RequirementEventAction>;

export const ConstraintKind = z.enum([
  'MUST_HAVE',
  'NICE_TO_HAVE',
  'EXCLUDE',
  'INFORMATIONAL',
]);
export type ConstraintKind = z.infer<typeof ConstraintKind>;

/**
 * WHAT KIND of demand a constraint is — a different axis from `ConstraintKind`,
 * which says how badly it is wanted.
 *
 * The two are orthogonal and routinely confused: "a bathroom" and "must have"
 * are not competing answers, they are answers to different questions. A scout
 * reads the facet ("what am I looking for?") and the kind ("can I compromise?")
 * separately, and collapsing them would make "nice-to-have bathroom" and
 * "must-have north light" unsortable against each other.
 *
 * The colours are the tagging surface's, not decoration: a producer marking up
 * a screenplay picks a facet by colour before reading its label, exactly as a
 * first AD reads a breakdown sheet by colour (see `ELEMENT_CATEGORIES`).
 */
export const RequirementFacet = z.enum([
  'SPACES_ENVIRONMENT',
  'VISUAL_ATTRIBUTES',
  'PHYSICAL_FEATURES',
  'CONDITIONS',
]);
export type RequirementFacet = z.infer<typeof RequirementFacet>;

export interface RequirementFacetMeta {
  readonly label: string;
  /** Background of the facet tab and of a span marked with it. */
  readonly colour: string;
  /** What belongs here, for the producer who is not sure which tab to pick. */
  readonly use: string;
}

export const REQUIREMENT_FACETS: Readonly<Record<RequirementFacet, RequirementFacetMeta>> =
  Object.freeze({
    SPACES_ENVIRONMENT: {
      label: 'Spaces & environment',
      colour: '#FBD5D5',
      use: 'The place itself: a bedroom, a hallway, a working harbour',
    },
    VISUAL_ATTRIBUTES: {
      label: 'Visual attributes',
      colour: '#FDE8B8',
      use: 'How it must look: bright, serene, brutalist, cluttered',
    },
    PHYSICAL_FEATURES: {
      label: 'Physical features',
      colour: '#E4E7EC',
      use: 'What it must have: large windows, a staircase, a jetty',
    },
    CONDITIONS: {
      label: 'Conditions',
      colour: '#CDF4DF',
      use: 'What must be true to shoot: power, night access, no flight path',
    },
  });

export const REQUIREMENT_FACET_ORDER: readonly RequirementFacet[] = Object.freeze([
  'SPACES_ENVIRONMENT',
  'VISUAL_ATTRIBUTES',
  'PHYSICAL_FEATURES',
  'CONDITIONS',
]);

/**
 * Where a claim came from. Rendered distinctly in every surface — the design's
 * four-way provenance (From script / AI inference / Human confirmed / Needs
 * confirmation) is this enum.
 */
export const Provenance = z.enum([
  'SCRIPT_FACT',
  'AI_INFERENCE',
  'HUMAN_DECISION',
  'UNKNOWN',
]);
export type Provenance = z.infer<typeof Provenance>;
